import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"

import Header from "../components/Header"
import Layout from "../components/Layout"
import FaqAccordion from "../components/FaqAccordion"
import IntegrationGuideStripe from "../components/integrations/IntegrationGuideStripe"
import PremagicNewsSection from "../components/PremagicNews"
import { btn, btnPrimary } from "../ds/button.module.css"
import { WhiteArrowRight } from "../ds/Shapes"
import { CheckIconGreen } from "../ds/SvgIcons"

export default function ShareEventPhotosUsingFaceRecognition() {
  return (
    <Layout
      title="Share Event Photos Using Face Recognition | Premagic"
      description="Share event photos using face recognition — Premagic matches every face to the right attendee and delivers their gallery automatically. No manual sorting, no missed photos."
      keywords={[
        "share event photos using face recognition, ai face recognition photo sharing, face recognition photo sharing, face matched photo delivery, event photo face recognition, ai photo sharing, attendee photo matching, automated event photo sharing, facial recognition event photos, personalized photo gallery",
      ]}
      breadcrumbs={[
        { name: "Home", url: "/" },
        {
          name: "Share Photos Using Face Recognition",
          url: "/share-event-photos-using-face-recognition/",
        },
      ]}
    >
      <div className="">
        <div className="" style={{ backgroundColor: "#7251F4" }}>
          <Header isWhiteLogo />
        </div>

        {/* Hero */}
        <section className="py-20" style={{ backgroundColor: "#7251F4" }}>
          <div className="px-5 md:px-0 mx-auto md:w-[1080px]">
            <div className="md:flex items-center md:h-[500px]">
              <div className="md:w-6/12 flex flex-col justify-center text-left">
                <h1 className="text-3xl md:text-5xl font-bold mb-4 text-white">
                  Share Event Photos
                  <span style={{ color: "#61F2C2" }}>
                    {" "}
                    Using Face Recognition
                  </span>
                </h1>
                <p className="text-white mb-5">
                  Stop sorting thousands of photos by hand. Premagic lets you
                  share event photos using face recognition — the AI identifies
                  each attendee and sends them their personalized gallery over
                  WhatsApp and email automatically, so everyone gets their
                  moments in minutes.
                </p>
                <Link
                  to="/request-a-demo/"
                  className={`${btn} ${btnPrimary} text-xs mt-5 mb-10 md:mb-0 relative w-max group`}
                >
                  <span className="mr-4 ml-2">Show me how</span>
                  <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
                </Link>
              </div>
              <div className="w-5/6 md:w-6/12 md:ml-0 relative">
                <StaticImage
                  src="../images/ai-distribution_hero.png"
                  alt="share-event-photos-using-face-recognition-hero"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="md:pt-10">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px]">
            <div className="flex flex-col md:flex-row flex-col-reverse py-10">
              <div className="md:w-6/12 flex flex-col justify-center text-left md:pr-10 md:mt-0">
                <h2
                  className="text-3xl md:text-4xl font-bold md:leading-snug mb-4"
                  style={{ color: "#7251F4" }}
                >
                  How face-recognition <br />
                  photo sharing works
                </h2>
                <p className="mb-5">
                  Photographers upload photos; the AI detects every face,
                  matches it to your registered attendees, and delivers each
                  person their own gallery instantly — no manual tagging, no
                  missed photos.
                </p>
                {[
                  "Upload event photos to Premagic",
                  "AI detects and matches every face across the gallery",
                  "Each attendee receives their personalized photos via WhatsApp & email",
                ].map((text, idx) => (
                  <div key={idx} className="flex items-start my-2">
                    <span className="flex-shrink-0 w-6 h-6">
                      <CheckIconGreen />
                    </span>
                    <p className="ml-3">{text}</p>
                  </div>
                ))}
                <Link
                  to="/request-a-demo/"
                  className={`${btn} ${btnPrimary} text-xs mt-8 mb-10 md:mb-0 relative w-max group`}
                >
                  <span className="mr-4 ml-2">Show me how</span>
                  <WhiteArrowRight className="transition duration-300 ease-in-out group-hover:translate-x-2 mr-2" />
                </Link>
              </div>
              <div className="md:w-6/12 md:pl-10 text-right mt-10 md:mt-0 -mr-10 md:mr-0">
                <StaticImage
                  src="../images/ai_dist_how_it_works.png"
                  alt="share-event-photos-using-face-recognition-how-it-works"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Metrics */}
        <section className="my-16">
          <div className="px-5 md:px-0 mx-auto flex py-10 md:items-center flex-wrap md:w-[1080px]">
            <div className="w-1/2 mb-16 md:mb-0 md:w-1/3 text-center">
              <StaticImage
                class="w-20 md:w-18"
                src="../images/ai_dist_metric_1.png"
                alt="share-event-photos-face-recognition-accuracy"
              />
              <h3
                style={{ color: "#FF6550" }}
                className="text-4xl font-bold my-5"
              >
                99.9%
              </h3>
              <p>
                facial recognition <br />
                accuracy
              </p>
            </div>
            <div className="w-1/2 mb-16 md:mb-0 md:w-1/3 text-center">
              <StaticImage
                class="w-20 md:w-20"
                src="../images/ai_dist_metric_2.png"
                alt="share-event-photos-face-recognition-events"
              />
              <h3
                style={{ color: "#FF6550" }}
                className="text-4xl font-bold my-5"
              >
                700K
              </h3>
              <p>
                events
                <br />
                powered
              </p>
            </div>
            <div className="w-1/2 md:w-1/3 text-center">
              <StaticImage
                class="w-20 md:w-20"
                src="../images/ai_dist_metric_3.png"
                alt="share-event-photos-face-recognition-engagement"
              />
              <h3
                style={{ color: "#FF6550" }}
                className="text-4xl font-bold my-5"
              >
                70%
              </h3>
              <p>
                increase in attendee
                <br />
                engagement
              </p>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="py-16 bg-gray-50">
          <div className="px-5 md:px-0 mx-auto md:w-[1080px]">
            <div className="text-center mb-10">
              <p
                className="text-sm font-semibold tracking-widest uppercase mb-3"
                style={{ color: "#7251F4" }}
              >
                Related
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-black">
                Explore the full photo experience
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/smart-photo-distribution-for-seamless-events/"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  AI Photo Distribution
                </h3>
                <p className="text-sm text-gray-600">
                  Automatically deliver face-matched galleries to every attendee
                  over WhatsApp and email.
                </p>
                <span
                  className="inline-flex items-center text-sm font-semibold mt-4 transition-transform duration-200 group-hover:translate-x-1"
                  style={{ color: "#7251F4" }}
                >
                  Learn more →
                </span>
              </Link>
              <Link
                to="/simplify-event-registration/"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  Registration with Face Check-In
                </h3>
                <p className="text-sm text-gray-600">
                  Register attendees and capture the selfie that powers accurate
                  face matching.
                </p>
                <span
                  className="inline-flex items-center text-sm font-semibold mt-4 transition-transform duration-200 group-hover:translate-x-1"
                  style={{ color: "#7251F4" }}
                >
                  Learn more →
                </span>
              </Link>
              <Link
                to="/digital-asset-management/"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
              >
                <h3 className="font-bold text-lg text-black mb-2">
                  Digital Asset Management
                </h3>
                <p className="text-sm text-gray-600">
                  Organize every event photo with AI and find any face in
                  seconds.
                </p>
                <span
                  className="inline-flex items-center text-sm font-semibold mt-4 transition-transform duration-200 group-hover:translate-x-1"
                  style={{ color: "#7251F4" }}
                >
                  Learn more →
                </span>
              </Link>
            </div>
          </div>
        </section>

        <FaqAccordion
          faqs={[
            {
              question: "Can I share event photos using face recognition?",
              answer:
                "Yes. Premagic's AI detects each attendee's face and automatically delivers their matching photos, no manual tagging required.",
            },
            {
              question: "Is face-recognition photo sharing accurate?",
              answer:
                "The AI matches faces across thousands of photos and improves with each event, delivering the right galleries to the right people.",
            },
            {
              question: "Is attendee photo data handled securely?",
              answer:
                "Yes. Photos are delivered privately to each matched attendee and are never shared with third parties. Face matching runs with attendee consent, and Premagic is GDPR and CCPA compliant.",
            },
          ]}
        />

        <PremagicNewsSection />
        <IntegrationGuideStripe context="Already using Cvent, HubSpot, Swapcard or Luma? Face-matched photos flow into your attendees' tickets automatically." />
      </div>
    </Layout>
  )
}
